import { __rest } from 'tslib';
import React__default, { useRef, useState, useEffect } from 'react';
import { Title } from './components/Typography.es.js';
import { CloseOutlined } from '@teko/ds-icon';
import ReactDOM from 'react-dom';
import { useTransition, animated } from 'react-spring';
import { SizeEnum } from '@teko/types/ds';
import { g as getScrollbarWidth, p as pixelParse, m as mergeCls, u as uid } from './utils-f1412689.js';
import { styled } from '@teko/styled';
import { B as Button } from './Button-6d1efb51.js';

const Overlay = ({ show, children, parent, onEscPressed, id, }) => {
    const elmRef = useRef();
    const [, setValue] = useState(0);
    const parentElement = typeof document !== 'undefined' ? parent || document.body : null;
    useEffect(() => {
        if (!show) {
            return;
        }
        const elm = document.createElement('div');
        if (id)
            elm.id = id;
        elm.style.position = 'fixed';
        elm.style.top = '0';
        elm.style.left = '0';
        elm.style.right = '0';
        elm.style.bottom = '0';
        elm.style.zIndex = '1070';
        elm.style.display = 'flex';
        elm.style.justifyContent = 'center';
        elm.style.alignItems = 'center';
        elm.style.background = 'rgba(0, 0, 0, 0.1)';
        elmRef.current = elm;
        parentElement.appendChild(elm);
        setValue((value) => value + 1);
        return () => {
            parentElement.removeChild(elm);
        };
    }, [show]);
    const handleDocumentKeyDown = (e) => {
        if (e.keyCode === 27 && onEscPressed) {
            onEscPressed();
        }
    };
    useEffect(() => {
        if (!show) {
            return;
        }
        document.addEventListener('keydown', handleDocumentKeyDown);
        return () => {
            document.removeEventListener('keydown', handleDocumentKeyDown);
        };
    }, [show]);
    if (!show || !elmRef.current) {
        return null;
    }
    return ReactDOM.createPortal(children, elmRef.current);
};

const Modal$1 = (_a) => {
    var { show, onHide, children, size = SizeEnum.m, backDropStyle, parent } = _a, props = __rest(_a, ["show", "onHide", "children", "size", "backDropStyle", "parent"]);
    const parentElement = typeof document !== 'undefined' ? parent || document.body : null;
    useEffect(() => {
        if (typeof document !== 'undefined') {
            if (show) {
                parentElement.style.overflow = 'hidden';
                parentElement.style.paddingRight = `${getScrollbarWidth()}px`;
            }
        }
        return () => {
            parentElement.style.overflow = 'unset';
            parentElement.style.paddingRight = 'unset';
        };
    }, [show]);
    if (!show)
        return null;
    return (React__default.createElement(Overlay, { parent: parentElement, show: show, onEscPressed: onHide },
        React__default.createElement(Backdrop$1, { onClick: onHide, style: backDropStyle }),
        React__default.createElement(ModalContent, Object.assign({ size: size }, props), children)));
};
const Backdrop$1 = styled.div `
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: -1000;
`;
const sizeMaps = {
    [SizeEnum.s]: '400px',
    [SizeEnum.m]: '700px',
    [SizeEnum.l]: '960px',
};
const ModalContent = styled.div(({ theme, size, width, maxHeight, borderRadius, backgroundColor, }) => `
    background-color: ${backgroundColor || theme.colors.white};
    display: flex;
    flex-direction: column;
    width: ${width !== undefined ? pixelParse(width) : sizeMaps[size]};
    border-radius: ${borderRadius || theme.borderRadius.m};
    max-height: ${maxHeight || '90%'};
    z-index: 1060;
    position: relative;
    overflow-y: auto;
  `);

const Modal = (_a) => {
    var { onClose, visible, title, children, footer = true, width, style, onOk, onCancel, okText, cancelText, okButtonProps, cancelButtonProps, closable = true, centeredTitle = false, alignFooter = 'right', fullWidthFooter = false } = _a, rest = __rest(_a, ["onClose", "visible", "title", "children", "footer", "width", "style", "onOk", "onCancel", "okText", "cancelText", "okButtonProps", "cancelButtonProps", "closable", "centeredTitle", "alignFooter", "fullWidthFooter"]);
    if (typeof (rest === null || rest === void 0 ? void 0 : rest.show) !== 'undefined') {
        const oldModalProps = Object.assign(Object.assign({}, rest), { children,
            width });
        return React__default.createElement(Modal$1, Object.assign({}, oldModalProps));
    }
    const closeOnEscapeKeyDown = (e) => {
        if ((e.charCode || e.keyCode) === 27) {
            if (typeof onClose === 'function')
                onClose();
        }
    };
    const transitions = useTransition(visible, {
        from: { opacity: 0, transform: 'translateY(-40px)' },
        enter: { opacity: 1, transform: 'translateY(0px)' },
        leave: { opacity: 0, transform: 'translateY(-40px)' },
    });
    useEffect(() => {
        document.body.addEventListener('keydown', closeOnEscapeKeyDown);
        return function cleanup() {
            document.body.removeEventListener('keydown', closeOnEscapeKeyDown);
        };
    }, []);
    useEffect(() => {
        if (typeof document !== 'undefined') {
            const currentModals = document.querySelectorAll('*[id^="teko-modal"]');
            if (currentModals.length) {
                document.body.style.overflow = 'hidden';
                document.body.style.paddingRight = `${getScrollbarWidth()}px`;
            }
        }
        return () => {
            document.body.style.overflow = 'unset';
            document.body.style.paddingRight = 'unset';
        };
    }, [visible]);
    const renderFooter = () => {
        if (footer === false || footer === null || footer === undefined) {
            return null;
        }
        const footerCls = mergeCls('teko-modal-footer', `teko-modal-footer-${alignFooter}`);
        if (footer === true) {
            let style;
            if (fullWidthFooter) {
                style = { flex: 1 };
            }
            return (React__default.createElement("div", { className: footerCls },
                React__default.createElement(Button, Object.assign({ marginRight: "s12", onClick: () => onCancel === null || onCancel === void 0 ? void 0 : onCancel() }, cancelButtonProps, { style: style }), cancelText || 'Cancel'),
                React__default.createElement(Button, Object.assign({ variant: "primary", onClick: () => onOk === null || onOk === void 0 ? void 0 : onOk() }, okButtonProps, { style: style }), okText || 'OK')));
        }
        return React__default.createElement("div", { className: footerCls }, footer);
    };
    const modalCls = mergeCls('teko-modal', visible ? 'teko-modal-show' : '');
    const modalTitleCls = mergeCls('teko-modal-title', centeredTitle ? 'teko-modal-title-centered' : '');
    return (React__default.createElement(Overlay, { show: visible, onEscPressed: onClose, id: `teko-modal-${uid()}` },
        React__default.createElement(Backdrop, { onClick: onClose }),
        transitions((styles, item) => item && (React__default.createElement(AnimatedModal, Object.assign({ className: modalCls, style: Object.assign(Object.assign(Object.assign({}, styles), (!!width ? { width } : {})), style) }, rest),
            React__default.createElement("div", { className: "teko-modal-content", onClick: (e) => e.stopPropagation() },
                closable && (React__default.createElement(CloseOutlined, { className: "teko-modal-close", size: 24, color: "#000000", onClick: () => onClose() })),
                title && (React__default.createElement("div", { className: "teko-modal-header" }, typeof title === 'string' ? (React__default.createElement(Title, { level: 1, className: modalTitleCls }, title)) : (React__default.createElement("div", { className: modalTitleCls }, title)))),
                React__default.createElement("div", { className: "teko-modal-body" },
                    children,
                    renderFooter())))))));
};
const Backdrop = styled.div `
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: -1000;
`;
const StyledModal = styled.div `
  width: 520px;
  &.teko-modal-show {
    opacity: 1;
    pointer-events: visible;
  }

  &.teko-modal-show .teko-modal-content {
    transform: translateY(0);
    z-index: 1050;
  }

  .teko-modal-content {
    padding: 24px;
    transition: all 0.2s ease-in-out;
    background-color: #ffffff;
    box-shadow: 0px 4px 16px rgba(0, 0, 0, 0.05),
      0px 8px 16px rgba(0, 0, 0, 0.05);
    position: relative;
    border-radius: 8px;
    position: relative;

    .teko-modal-close {
      position: absolute;
      z-index: 1000;
      top: 24px;
      right: 24px;
      cursor: pointer;
    }

    .teko-modal-header {
      .teko-modal-title {
        &.teko-modal-title-centered {
          text-align: center;
        }
      }
    }

    .teko-modal-body {
      margin-top: 24px;
      font-size: 13px;
      line-height: 20px;
      word-wrap: break-word;
    }

    .teko-modal-footer {
      margin-top: 24px;
      &.teko-modal-footer-right {
        display: flex;
        justify-content: flex-end;
      }
      &.teko-modal-footer-left {
        display: flex;
        justify-content: flex-start;
      }
      &.teko-modal-footer-center {
        display: flex;
        justify-content: center;
      }
    }
  }
`;
const AnimatedModal = animated(StyledModal);

const initialState = {};
const ModalStateContext = React__default.createContext(initialState);
const ModalProvider = ({ children, }) => {
    const [configModals, setConfigModals] = useState([]);
    const showModal = (options) => {
        const { onCancel: baseOnCancel, onOk: baseOnOk } = options, rest = __rest(options, ["onCancel", "onOk"]);
        const modalId = uid();
        const onClose = () => {
            setConfigModals((prev) => {
                const newConfigs = prev.map((m) => {
                    if (m.modalId === modalId)
                        return Object.assign(Object.assign({}, m), { visible: false });
                    return m;
                });
                if (newConfigs.every((m) => !m.visible))
                    return [];
                return newConfigs;
            });
        };
        const onCancel = () => {
            baseOnCancel === null || baseOnCancel === void 0 ? void 0 : baseOnCancel();
            onClose();
        };
        const onOk = async () => {
            await (baseOnOk === null || baseOnOk === void 0 ? void 0 : baseOnOk());
            onClose();
        };
        setConfigModals((prev) => [
            ...prev,
            Object.assign({ modalId, visible: true, onClose,
                onCancel,
                onOk }, rest),
        ]);
        return modalId;
    };
    const closeAllModals = () => {
        setConfigModals([]);
    };
    return (React__default.createElement(ModalStateContext.Provider, { value: {
            showModal,
            closeAllModals,
        } },
        children,
        configModals.map((modal) => (React__default.createElement(Modal, Object.assign({ key: modal.modalId }, modal))))));
};

export { Modal as M, Overlay as O, ModalStateContext as a, ModalProvider as b };
